import { getPortalBearerToken } from '@app/utils/portalAuth';
import { API_BASE_URL } from '../../config/bootstrap';

export interface S3File {
  reportName: string;
  filePath: string;
  lastModified: Date;
  size: number;
  state?: string;
  requestId?: string;
  productName?: string;
  requestTime?: string;
  displayName?: string;
  contactEmail?: string;
}

export const formatRequestOwner = (file: Pick<S3File, 'displayName' | 'contactEmail'>): string => {
  const name = (file.displayName || '').trim();
  const email = (file.contactEmail || '').trim();
  if (name && email && name !== email) {
    return `${name} (${email})`;
  }
  return email || name || '—';
};

export const listAccessibleFiles = async (): Promise<S3File[]> => {
  try {
    const idToken = await getPortalBearerToken();
    if (!idToken) {
      console.error('No ID token available in session');
      throw new Error('No ID token available');
    }

    const response = await fetch(`${API_BASE_URL}/reports/requests`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to list requests: ${response.status}`);
    }

    const data = await response.json();
    const items = Array.isArray(data.items) ? data.items : [];
    return items.map((item: any) => {
      const requestId = String(item.RequestId ?? '');
      const productName = String(item.ProductName ?? 'Report');
      const requestTime = String(item.RequestTime ?? '');
      return {
        reportName: productName,
        filePath: requestId,
        lastModified: requestTime ? new Date(requestTime) : new Date(0),
        size: 0,
        state: String(item.State ?? ''),
        requestId,
        productName,
        requestTime,
        displayName: String(item.displayName ?? ''),
        contactEmail: String(item.contactEmail ?? ''),
      };
    });
  } catch (error: any) {
    console.error('Error in listAccessibleFiles:', error);
    console.error('Error details:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack
    });
    return [];
  }
};

export const readFileContents = async (filePath: string): Promise<any> => {
  try {
    const idToken = await getPortalBearerToken();
    if (!idToken) {
      throw new Error('No ID token available');
    }

    const urlResp = await fetch(
      `${API_BASE_URL}/reports/output?requestId=${encodeURIComponent(filePath)}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );
    if (!urlResp.ok) {
      const errText = await urlResp.text();
      throw new Error(errText || `Failed to get report URL (${urlResp.status})`);
    }

    const urlPayload = await urlResp.json();
    const signedUrl = urlPayload.url;
    if (!signedUrl) {
      throw new Error('No download URL returned');
    }

    const fileResp = await fetch(signedUrl, { method: 'GET' });
    if (!fileResp.ok) {
      throw new Error(`Failed to download report payload (${fileResp.status})`);
    }
    const text = await fileResp.text();
    return JSON.parse(text);
  } catch (error: any) {
    console.error('Error reading file from S3:', {
      error,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStack: error?.stack,
      filePath,
    });
    throw error;
  }
}; 